import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Accordion } from '@zillow/constellation-10';

import { AdproductsConstants, SalesforceProductCodes } from '@zg-rentals/rental-revenue-components';
import {
  EnterpriseListingEvent,
  FeedOverrideSection,
  RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent,
} from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { ACCORDION_OPEN_STATE, useAccordionState } from '../../contexts/AccordionStateContext';
import { useGetListingDetails, useGetListingRecommendations } from '../../hooks/queries/properties';
import { getAccordionIndexFromRecs, getAccordionTag } from './utils';

import ApplicationsPermissionSetupModal from '../modals/ApplicationsPermissionSetupModal';
import RecommendationModal from '../modals/RecommendationModal';
import ApplicationsContent from './components/ApplicationsContent';
import BuildingDetailsContent from './components/BuildingDetailsContent';
import ContactContent from './components/ContactContent';
import CostsAndFeesContent from './components/CostsAndFeesContent';
import FloorPlansContent from './components/FloorPlansContent';
import KeyFeaturesContent from './components/KeyFeaturesContent';
import OtherPropertyFeaturesContent from './components/OtherPropertyFeaturesContent';
import PoliciesAndTermsContent from './components/PoliciesAndTermsContent';
import ScreeningCriteriaContent from './components/ScreeningCriteriaContent';
import SpecialOffersContent from './components/SpecialOffersContent';
import { SponsoredAdsContent } from './components/SponsoredAdsContent';
import UnitFeaturesContent from './components/UnitFeaturesContent';

import { EVENT_IDS } from '../../analytics/events/reviewPage';
import costsAndFeesConstraints from '../../validation/costsandFeesConstraints';
import AccordionContentContainer from '../AccordionContentContainer';
import { useAuth } from '../AuthProvider';
import BuildingMediaContent from '../PostingPath/MediaContent';

import type { ContentSectionStatus } from '../../utils/accordionStatusUtils';

export const MFAccordion = ({
  requiredFieldsErrors,
}: {
  requiredFieldsErrors: Record<string, ContentSectionStatus>;
}) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { verifyIdentitySuccess } = router.query;

  const { user } = useAuth();

  const { openState } = useAccordionState();

  const { data: listingDetailsData } = useGetListingDetails(listingAlias);

  const { recommendationsData } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: listingDetailsData?.isMultifamily,
  });

  const { data: costsAndFeesData } = useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get(
    { listingAlias },
    { enabled: Boolean(listingAlias && listingDetailsData?.isMultifamily), meta: { tags: ['mf-costsAndFees'] } },
  );

  const [expandedIndices, setExpandedIndices] = useState<Array<string>>([]);
  const [displayRecsModal, setDisplayRecsModal] = useState<string | null>(null);
  const [showAppsPermissionModal, setShowAppsPermissionModal] = useState(
    verifyIdentitySuccess === 'true' && user?.features?.canAccessBusinessVerificationLite,
  );

  const productTypes = listingDetailsData?.adproduct?.productTypes || [];
  const hasBoost = productTypes.some((pkg: string) => pkg === SalesforceProductCodes.BOOST);
  const isBasePackage = productTypes.some((pkg: string) => pkg === AdproductsConstants.ProductCodes.BASE_PACKAGE);
  const shouldDisplayBoostAds = (user?.impersonatorEmail || user?.impersonatorUserToken) && hasBoost && !isBasePackage;

  const recommendationsList = recommendationsData?.recommendations || [];

  const numContentSections = 14;
  const allExpandedArray = useMemo(() => [...Array(numContentSections).keys()].map(String), [numContentSections]);

  const initialRender = useRef(true);

  useEffect(() => {
    if (!initialRender.current) {
      if (
        user?.customers.length &&
        !user?.impersonatorEmail &&
        !user?.impersonatorUserToken &&
        listingDetailsData?.feedId &&
        listingDetailsData?.feedListingId
      ) {
        RentalManagerEnterpriseEventTrackingWebApiHandler_trackCustomerListingEvent({
          feedId: listingDetailsData?.feedId,
          feedListingId: listingDetailsData?.feedListingId,
          event: EnterpriseListingEvent.VIEW_PROPERTY,
        }).catch(() => {});
      }
    }
  }, [user, listingDetailsData]);

  useEffect(() => {
    if (costsAndFeesData?.dictionaryItems) {
      costsAndFeesConstraints.getcostsAndFeesConstraints(costsAndFeesData?.dictionaryItems);
    }
  }, [costsAndFeesData?.dictionaryItems]);

  useEffect(() => {
    setExpandedIndices(openState === ACCORDION_OPEN_STATE.EXPAND_ALL ? allExpandedArray : []);
  }, [openState, allExpandedArray]);

  useEffect(() => {
    const { scrollTo } = router.query;

    if (scrollTo && typeof scrollTo === 'string') {
      const index = getAccordionIndexFromRecs(scrollTo);

      if (index !== null) {
        setExpandedIndices([index.toString()]);

        requestAnimationFrame(() => {
          const element = document.querySelector(`[data-accordion-index="${index}"]`);
          if (element) {
            const rect = element.getBoundingClientRect();
            const scrollTop = window.pageYOffset + rect.top - 152; // account for sticky header

            window.scrollTo({
              top: scrollTop,
              behavior: 'smooth',
            });
          }

          // Clean up query param after scroll completes
          const { scrollTo: _, listingAlias: __, ...restQuery } = router.query;
          const pathname = router.asPath.split('?')[0];
          // @ts-expect-error pathname is not typed
          router.replace({ pathname, query: restQuery }, undefined, { shallow: true });
        });
      }
    }
  }, [router.query.scrollTo]);

  const handleExpandedChange = (values: string | Array<string>) => {
    const newIndices = typeof values === 'string' ? [values] : values;
    setExpandedIndices(newIndices);
  };

  const isFeed = Boolean(listingDetailsData?.isFeed);

  return (
    <div data-testid="MFAccordionContainer">
      <Accordion.Root expanded={expandedIndices} onExpandedChange={handleExpandedChange}>
        <AccordionContentContainer
          header="Building details"
          content={<BuildingDetailsContent onRecsClick={setDisplayRecsModal} />}
          tag={getAccordionTag({ ...requiredFieldsErrors.buildingDetails, recommendationsList })}
          accordionIndex={0}
          isFeed={isFeed}
          feedOverrideSection={FeedOverrideSection.BUILDING_DETAILS}
        />

        <AccordionContentContainer
          header="Contact"
          content={<ContactContent onRecsClick={setDisplayRecsModal} />}
          tag={getAccordionTag({ ...requiredFieldsErrors.contact, recommendationsList })}
          accordionIndex={1}
          isFeed={isFeed}
          feedOverrideSection={FeedOverrideSection.CONTACT}
        />

        {shouldDisplayBoostAds && (
          <AccordionContentContainer
            tag={getAccordionTag({ ...requiredFieldsErrors.sponsoredAds, recommendationsList })}
            header="Sponsored ads"
            content={<SponsoredAdsContent onOpenBuildingMedia={() => handleExpandedChange(['3'])} />}
            accordionIndex={2}
          />
        )}

        <AccordionContentContainer
          header="Building media"
          content={<BuildingMediaContent onRecsClick={setDisplayRecsModal} />}
          tag={getAccordionTag({ ...requiredFieldsErrors.buildingMedia, recommendationsList })}
          accordionIndex={3}
          isFeed={isFeed}
          feedOverrideSection={FeedOverrideSection.MEDIA}
        />

        <AccordionContentContainer
          header="Floor plans"
          content={<FloorPlansContent onRecsClick={setDisplayRecsModal} />}
          tag={getAccordionTag({ ...requiredFieldsErrors.floorPlans, recommendationsList })}
          accordionIndex={4}
          isFeed={isFeed}
          feedOverrideSection={FeedOverrideSection.FLOOR_PLANS}
        />

        <AccordionContentContainer
          header="Special offers"
          content={<SpecialOffersContent onRecsClick={setDisplayRecsModal} />}
          tag={getAccordionTag({ ...requiredFieldsErrors.specialOffers, recommendationsList })}
          accordionIndex={5}
          isFeed={isFeed}
          feedOverrideSection={FeedOverrideSection.SPECIAL_OFFERS}
        />

        <AccordionContentContainer
          tag={getAccordionTag({ ...requiredFieldsErrors.costsAndFees, recommendationsList })}
          header="Costs and fees"
          content={<CostsAndFeesContent />}
          accordionIndex={6}
          isFeed={isFeed}
          feedOverrideSection={FeedOverrideSection.COSTS_AND_FEES}
        />

        <AccordionContentContainer
          header="Key features"
          content={<KeyFeaturesContent onRecsClick={setDisplayRecsModal} />}
          tag={getAccordionTag({ ...requiredFieldsErrors.keyFeatures, recommendationsList })}
          accordionIndex={7}
          isFeed={isFeed}
          feedOverrideSection={FeedOverrideSection.AMENITIES}
        />

        <AccordionContentContainer
          header="Unit features"
          content={<UnitFeaturesContent />}
          tag={getAccordionTag({ ...requiredFieldsErrors.unitFeatures, recommendationsList })}
          accordionIndex={8}
          isFeed={isFeed}
          feedOverrideSection={FeedOverrideSection.AMENITIES}
        />

        <AccordionContentContainer
          header="Other property features"
          content={<OtherPropertyFeaturesContent onRecsClick={setDisplayRecsModal} />}
          tag={getAccordionTag({ ...requiredFieldsErrors.otherPropertyFeatures, recommendationsList })}
          accordionIndex={9}
          isFeed={isFeed}
          feedOverrideSection={FeedOverrideSection.AMENITIES}
        />

        <AccordionContentContainer
          header="Policies and terms"
          content={<PoliciesAndTermsContent onRecsClick={setDisplayRecsModal} />}
          tag={getAccordionTag({ ...requiredFieldsErrors.policiesAndTerms, recommendationsList })}
          accordionIndex={10}
          isFeed={isFeed}
          feedOverrideSection={FeedOverrideSection.POLICIES}
        />

        {listingDetailsData?.applicationsSummary?.listingAlias && user?.features?.canAccessBusinessVerificationLite && (
          <AccordionContentContainer
            header="Applications"
            content={<ApplicationsContent setShowAppsPermissionModal={setShowAppsPermissionModal} />}
            eventId={EVENT_IDS.APPLICATIONS_PANEL_OPEN}
            accordionIndex={11}
          />
        )}
        {listingDetailsData?.listingCriteria?.isEnabled && (
          <AccordionContentContainer
            tag={getAccordionTag({ ...requiredFieldsErrors.screeningCriteria, recommendationsList })}
            header="Screening criteria"
            content={<ScreeningCriteriaContent onRecsClick={setDisplayRecsModal} />}
            accordionIndex={13}
            isFeed={isFeed}
          />
        )}
        {displayRecsModal && (
          <RecommendationModal onClose={() => setDisplayRecsModal(null)} recommendationType={displayRecsModal} />
        )}
        {showAppsPermissionModal && (
          <ApplicationsPermissionSetupModal
            setShowApplicationsPermissionSetupModal={setShowAppsPermissionModal}
            verifyIdentitySuccess={Boolean(
              verifyIdentitySuccess === 'true' && user?.firstName && user?.lastName && user?.isIdVerified,
            )}
            propertyAddress={{
              street: listingDetailsData?.street,
              city: listingDetailsData?.city,
              state: listingDetailsData?.state,
              zip: listingDetailsData?.zip,
              unit: listingDetailsData?.unit,
            }}
          />
        )}
      </Accordion.Root>
    </div>
  );
};
